

import React, { useState, useEffect } from "react";
import { bookRide } from "../../services/api";
import { useNavigate } from "react-router-dom";
import {
  TextField,
  MenuItem,
  Button,
  Box,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  LocalizationProvider,
  DatePicker,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useAuth } from "../../components/AuthContext";
import { tokenStorage } from "../../utils/tokenStorage";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

const passengerOptions = [1, 2, 3, 4, 5, 6];
const genders = ["Male", "Female", "Other"];

// ✅ Empty defaults — no hardcoded test data
const initialFormState = {
  userId: "",
  name: "",
  contactNumber: "",
  email: "",
  homeAddress: "",
  officeAddress: "",
  gender: "",
  date: null,
  arrivalTime: null,
  departureTime: null,
  rideDuration: 0,
  distance: "",
  fare: 0,
  passengerCount: 1,
};

function RideBooking({ onSubmit }) {
  const { userId } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ ...initialFormState, userId: userId || "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  // ✅ Prefill form from logged-in user data (not hardcoded values)
  useEffect(() => {
    const userData = tokenStorage.getUser();
    if (userData) {
      setForm((prev) => ({
        ...prev,
        userId: userData.userId || userId || "",
        name: userData.userName || "",
        contactNumber: userData.userContactNumber || "",
        email: userData.userEmail || "",
        homeAddress: userData.userAddress || "",
        officeAddress: userData.userOfficeAddress || "",
        gender: userData.userGender || "",
      }));
    }
  }, [userId]);

  useEffect(() => {
    const dist = parseFloat(form.distance);
    if (!isNaN(dist) && dist >= 0) {
      setForm((prev) => ({ ...prev, fare: dist * 10 + 48.5 }));
    } else {
      setForm((prev) => ({ ...prev, fare: 0 }));
    }
  }, [form.distance]);

  useEffect(() => {
    if (form.arrivalTime && form.departureTime) {
      const durationMs = new Date(form.departureTime) - new Date(form.arrivalTime);
      const minutes = durationMs / (1000 * 60);
      setForm((prev) => ({ ...prev, rideDuration: Math.max(0, minutes) }));
    }
  }, [form.arrivalTime, form.departureTime]);

  const validate = () => {
    const newErrors = {};
    const requiredFields = [
      "name",
      "contactNumber",
      "email",
      "homeAddress",
      "officeAddress",
      "gender",
      "date",
      "arrivalTime",
      "departureTime",
      "distance",
    ];

    requiredFields.forEach((field) => {
      if (!form[field]) newErrors[field] = "This field is required";
    });

    if (form.contactNumber && !/^\d{10}$/.test(form.contactNumber)) {
      newErrors.contactNumber = "Enter a valid 10-digit number";
    }

    if (form.email && !/\S+@\S+\.\S+/.test(form.email)) {
      newErrors.email = "Enter a valid email";
    }

    if (form.distance && (isNaN(parseFloat(form.distance)) || parseFloat(form.distance) <= 0)) {
      newErrors.distance = "Enter valid distance";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field) => (event) => {
    setForm({ ...form, [field]: event.target.value });
    setErrors({ ...errors, [field]: null });
  };

  const handleSubmit = async () => {
    if (!userId) {
      setSnackbar({ open: true, message: "Login required", severity: "error" });
      return;
    }

    if (!validate()) {
      setSnackbar({
        open: true,
        message: "Please fix errors before submitting",
        severity: "error",
      });
      return;
    }

    setLoading(true);
    try {
      // ✅ Format date as ISO datetime (no microseconds)
      const formattedDate = form.date
        ? dayjs(form.date).format("YYYY-MM-DDTHH:mm:ss")
        : "";

      // ✅ Combine date + time into full LocalDateTime strings (matching backend)
      const dateOnly = form.date ? dayjs(form.date).format("YYYY-MM-DD") : "";
      const formattedArrivalTime = form.arrivalTime
        ? `${dateOnly}T${dayjs(form.arrivalTime).format("HH:mm:ss")}`
        : "";
      const formattedDepartureTime = form.departureTime
        ? `${dateOnly}T${dayjs(form.departureTime).format("HH:mm:ss")}`
        : "";

      const payload = {
        userId: form.userId,
        contactNumber: form.contactNumber,
        name: form.name,
        email: form.email,
        companyName: "N/A",
        homeAddress: form.homeAddress,
        occupation: "N/A",
        officeAddress: form.officeAddress,
        emergencyContactNumber: "0000000000",
        gender: form.gender,
        fromDate: formattedDate,
        toDate: formattedDate,
        officeArrivalTime: formattedArrivalTime,
        officeDepartureTime: formattedDepartureTime,
        rideDuration: parseInt(form.rideDuration, 10),
        fare: parseFloat(form.fare),
        distance: parseFloat(form.distance),
        passengerCount: Number(form.passengerCount),
      };

      console.log("Sending booking payload:", JSON.stringify(payload, null, 2));

      const response = await bookRide(payload);

      if (response?.status === 200) {
        setSnackbar({
          open: true,
          message: "Ride booking submitted!",
          severity: "success",
        });

        if (onSubmit) {
          await onSubmit(response.data);
        } else {
          setForm({ ...initialFormState, userId });
        }

        navigate(`/payment?rideBookingId=${response.data.data.bookingId}`);
      } else {
        throw new Error("Booking failed");
      }
    } catch (error) {
      console.error("Booking failed:", error.response?.status, error.response?.data);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Error during booking: " + error.message,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ maxWidth: 600, mx: "auto", p: 2 }}>
        <Typography variant="h5" mb={2}>
          Ride Booking
        </Typography>

        <TextField fullWidth label="Name" value={form.name} onChange={handleChange("name")} error={!!errors.name} helperText={errors.name} margin="normal" />
        <TextField fullWidth label="Contact Number" value={form.contactNumber} onChange={handleChange("contactNumber")} error={!!errors.contactNumber} helperText={errors.contactNumber} margin="normal" inputProps={{ maxLength: 10 }} />
        <TextField fullWidth label="Email" value={form.email} onChange={handleChange("email")} error={!!errors.email} helperText={errors.email} margin="normal" />
        <TextField fullWidth label="Home Address" value={form.homeAddress} onChange={handleChange("homeAddress")} error={!!errors.homeAddress} helperText={errors.homeAddress} margin="normal" />
        <TextField fullWidth label="Office Address" value={form.officeAddress} onChange={handleChange("officeAddress")} error={!!errors.officeAddress} helperText={errors.officeAddress} margin="normal" />
        <TextField select fullWidth label="Gender" value={form.gender} onChange={handleChange("gender")} error={!!errors.gender} helperText={errors.gender} margin="normal">
          {genders.map((g) => (
            <MenuItem key={g} value={g}>{g}</MenuItem>
          ))}
        </TextField>

        <DatePicker
          label="Date of Ride"
          value={form.date}
          onChange={(newValue) => {
            setForm({ ...form, date: newValue });
            setErrors({ ...errors, date: null });
          }}
          renderInput={(params) => (
            <TextField {...params} fullWidth margin="normal" error={!!errors.date} helperText={errors.date} />
          )}
        />

        <TimePicker
          label="Arrival Time"
          value={form.arrivalTime}
          onChange={(newValue) => {
            setForm({ ...form, arrivalTime: newValue });
            setErrors({ ...errors, arrivalTime: null });
          }}
          renderInput={(params) => (
            <TextField {...params} fullWidth margin="normal" error={!!errors.arrivalTime} helperText={errors.arrivalTime} />
          )}
        />
        <TimePicker
          label="Departure Time"
          value={form.departureTime}
          onChange={(newValue) => {
            setForm({ ...form, departureTime: newValue });
            setErrors({ ...errors, departureTime: null });
          }}
          renderInput={(params) => (
            <TextField {...params} fullWidth margin="normal" error={!!errors.departureTime} helperText={errors.departureTime} />
          )}
        />

        <TextField fullWidth label="Distance (km)" value={form.distance} onChange={handleChange("distance")} error={!!errors.distance} helperText={errors.distance} margin="normal" inputProps={{ inputMode: "decimal" }} />
        <TextField fullWidth label="Fare" value={form.fare.toFixed(2)} InputProps={{ readOnly: true }} margin="normal" />
        <TextField select fullWidth label="Passengers" value={form.passengerCount} onChange={(e) => handleChange("passengerCount")({ target: { value: Number(e.target.value) } })} margin="normal">
          {passengerOptions.map((num) => (
            <MenuItem key={num} value={num}>{num}</MenuItem>
          ))}
        </TextField>

        <Box mt={3}>
          <Button variant="contained" color="primary" fullWidth onClick={handleSubmit} disabled={loading} size="large">
            {loading ? <CircularProgress size={24} color="inherit" /> : "Request to Book"}
          </Button>
        </Box>

        <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
}

export default RideBooking;
